/**
 * NanomeModel: the columnar molecular model Lite renders and filters. Mirrors Nanome.Domain/Model/Data/ModelData.cs
 * (struct-of-arrays) and the Workspace API wire form so that filters, colourings and default components behave
 * like the server's. One NanomeModel per entry; frames hold coordinates for multi-model files.
 */

/** Nanome.Domain/Substructures/SubstructureType.cs */
export const Substructure = {
  Unknown: 0, Protein: 1, Nucleic: 2, Ligand: 3, Sugar: 4, Solvent: 5, Ion: 6, NonStandardProtein: 7, NonStandardNucleic: 8,
} as const;
export type SubstructureType = (typeof Substructure)[keyof typeof Substructure];

export const isProtein = (t: number) => t === Substructure.Protein || t === Substructure.NonStandardProtein;
export const isNucleic = (t: number) => t === Substructure.Nucleic || t === Substructure.NonStandardNucleic;
export const isPolymer = (t: number) => isProtein(t) || isNucleic(t);

/** Bond order as the server stores it (CovalentBond.BondType): 1 single, 2 double, 3 triple; aromatic is a separate flag. */
export interface Bonds {
  count: number;
  a: Uint32Array;        // atom index
  b: Uint32Array;        // atom index, a < b
  order: Uint8Array;     // 1 | 2 | 3
  aromatic: Uint8Array;  // 0 | 1
}

export interface Residues {
  count: number;
  /** first atom index and atom count per residue; atoms of a residue are contiguous */
  atomStart: Uint32Array;
  atomCount: Uint32Array;
  chainIndex: Uint32Array;
  name: string[];
  serial: Int32Array;
  insCode: string[];        // '' when none
  /** SecondaryStructure enum value per residue (NotAssigned = -1, Loop = 0, ...) */
  ss: Int8Array;
  /** majority SubstructureType of the residue's atoms */
  substructure: Uint8Array;
}

export interface Chains {
  count: number;
  /** unique chain names in first-appearance order; index = chain-instance index */
  name: string[];
  residueStart: Uint32Array;
  residueCount: Uint32Array;
}

/**
 * Residues of the full sequence that have no atoms (Nanome.Domain MissingSequence). mmCIF: rows of
 * pdbx_unobs_or_zero_occ_residues (MmcifSequenceExtractor.cs); PDB: serial gaps inside a chain, named '-'
 * (PdbSequenceExtractor.cs). Parallel arrays, ordered by chain name then serial.
 */
export interface MissingResidues {
  chain: string[];
  name: string[];
  serial: number[];
  insCode: string[];
}

/** MissingSequence.MissingResidueName: a gap whose residue name is unknown. */
export const MISSING_RESIDUE_NAME = '-';

export interface NanomeModel {
  atomCount: number;
  /** file atom serial (PDB serial / mmCIF id / 1-based index for SDF) */
  atomSerial: Int32Array;
  atomName: string[];
  /** atomic number; 0 when unknown */
  element: Uint8Array;
  /** current frame coordinates, Å, interleaved xyz */
  xyz: Float32Array;
  /** all frames (frames[0] === xyz initially); each 3*atomCount */
  frames: Float32Array[];
  residueIndex: Uint32Array;
  chainIndex: Uint32Array;
  altLoc: string[];           // '' when none
  bfactor: Float32Array;
  occupancy: Float32Array;
  /** 0 = ATOM, 1 = HETATM */
  hetero: Uint8Array;
  formalCharge: Int8Array;
  /** SubstructureType per atom */
  substructure: Uint8Array;
  bonds: Bonds;
  residues: Residues;
  chains: Chains;
  /** per-atom bond count (for NonBondedOnly) */
  bondCount: Uint16Array;
  /** true when atom is a hydrogen bonded to carbon (hidden by PolarOnly) */
  isNonPolarHydrogen: Uint8Array;
  /** min/max B-factor over the model (for ColdToWarm normalisation) */
  bfactorRange: [number, number];
  occupancyRange: [number, number];
  /** Unobserved residues for the sequence view; absent for small molecules and hand-built models. */
  missingResidues?: MissingResidues;
}

/** Composition summary in the shape of GET /workspaces/{id}/composition (GetWorkspaceComposition.cs), per entry. */
export interface ChainComposition {
  name: string;
  atomCount: number;
  residueCount: number;
  residueSerialRanges: [number, number][];
  substructureAtomCounts: Partial<Record<keyof typeof Substructure, number>>;
  nonStandardResidueNames: string[];
  hetResidues: { name: string; serial: number; insCode: string; atomCount: number; kind: 'ligand' | 'ion' | 'sugar' | 'solvent' | 'unknown' }[];
  hasAltLocs: boolean;
  residuesWithInsertionCodes: number;
}

export interface EntryComposition {
  entrySerial: number;
  name: string;
  modelCount: number;
  atomCount: number;
  chains: ChainComposition[];
}
